import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';

interface ConfirmationModalProps {
    isOpen: boolean;
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    onConfirm: () => void;
    onCancel: () => void;
    variant?: 'danger' | 'warning' | 'info' | 'primary';
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
    isOpen,
    title,
    message,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    onConfirm,
    onCancel,
    variant = 'warning',
}) => {
    const variants = {
        danger: {
            icon: 'text-error-600',
            bgIcon: 'bg-error-50',
            button: 'bg-error-600 hover:bg-error-700 focus:ring-error-500 shadow-lg shadow-error-500/20',
        },
        warning: {
            icon: 'text-warning-600',
            bgIcon: 'bg-warning-50',
            button: 'bg-warning-500 hover:bg-warning-600 focus:ring-warning-500 shadow-lg shadow-warning-500/20',
        },
        info: {
            icon: 'text-blue-600',
            bgIcon: 'bg-blue-50',
            button: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500 shadow-lg shadow-blue-500/20',
        },
        primary: {
            icon: 'text-accent-primary',
            bgIcon: 'bg-accent-primary/10',
            button: 'bg-accent-primary hover:bg-accent-primary/90 focus:ring-accent-primary shadow-lg shadow-accent-primary/20',
        },
    };

    const currentVariant = variants[variant];

    const titleRef = React.useRef(null);

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-[100]" onClose={onCancel} initialFocus={titleRef}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 modal-backdrop" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="relative bg-white dark:bg-neutral-900 rounded-2xl shadow-2xl max-w-sm w-full p-8 transform transition-all border border-neutral-100 dark:border-neutral-800">
                                {/* Icon */}
                                <div className={`flex items-center justify-center w-16 h-16 mx-auto mb-6 ${currentVariant.bgIcon} rounded-full transition-colors duration-300`}>
                                    <span className={`w-8 h-8 ${currentVariant.icon}`} style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_63308e57.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_63308e57.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                </div>

                                {/* Content */}
                                <div className="text-center mb-8">
                                    <Dialog.Title
                                        as="h3"
                                        className="text-xl font-bold text-neutral-900 dark:text-neutral-100 mb-2 focus:outline-none"
                                        ref={titleRef}
                                        tabIndex={-1}
                                    >
                                        {title}
                                    </Dialog.Title>
                                    <p className="text-sm text-neutral-500 dark:text-neutral-400 leading-relaxed">{message}</p>
                                </div>

                                {/*Buttons */}
                                <div className="flex gap-3">
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onCancel();
                                        }}
                                        className="flex-1 px-4 py-3 text-sm font-bold text-neutral-500 dark:text-neutral-400 bg-neutral-50 dark:bg-neutral-800 hover:bg-neutral-100 dark:hover:bg-neutral-700 border border-neutral-200 dark:border-neutral-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-neutral-200 dark:focus:ring-neutral-700 transition-all active:scale-[0.98]"
                                    >
                                        {cancelText}
                                    </button>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onConfirm();
                                        }}
                                        className={`flex-1 px-4 py-3 text-sm font-bold text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all shadow-lg active:scale-[0.98] ${currentVariant.button}`}
                                    >
                                        {confirmText}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};
